import { useCallback, useContext, useMemo } from 'react';
import {
  HCP_ROLES,
  ROLE_ADMINISTRATOR,
  ROLE_AUDITIVE_LEVEL_2,
  ROLE_AUDITIVE_LEVEL_3,
  ROLE_NURSE_BIRTH_CENTRE,
  ROLE_NURSE_NC,
  ROLE_OBSTETRICIAN,
  ROLE_SUPPORT,
} from '../constants/roles';
import { AuthContext } from '../context/AuthContext';
import { hasElement } from '../helpers/Array';
import { getRoleLabel, getRequiredHcpFields } from '../helpers/User';

export default function useUser() {
  const { user, isAuth, refresh, setActiveAccount, getActiveAccount } = useContext(AuthContext);

  const isAdmin = useMemo(() => hasElement(ROLE_ADMINISTRATOR, user.roles), [user.roles]);
  const isSupport = useMemo(() => hasElement(ROLE_SUPPORT, user.roles), [user.roles]);

  /**
   * Check if the current user has a role (or roles)
   *
   * @param {string|string[]} role
   * @returns {Boolean}
   */
  const hasRole = useCallback(
    (role) => isAdmin || isSupport || hasElement(role, user.roles),
    [user.roles, isAdmin, isSupport]
  );

  /**
   * Check if the current user has a permission (or permissions)
   *
   * @param {string|string[]} permission
   * @returns {Boolean}
   */
  const hasPermission = useCallback(
    (permission) => isAdmin || isSupport || hasElement(permission, user.permissions),
    [user.permissions, isAdmin, isSupport]
  );

  const hasCompleteProfile = useMemo(() => {
    if (isAdmin || isSupport) return true;

    const isNotPhysician = hasRole([
      ROLE_NURSE_BIRTH_CENTRE,
      ROLE_NURSE_NC,
      ROLE_OBSTETRICIAN,
      ROLE_AUDITIVE_LEVEL_2,
      ROLE_AUDITIVE_LEVEL_3,
    ]);
    const requiredHcpFields = getRequiredHcpFields(isNotPhysician);

    if (!hasRole(HCP_ROLES)) return true;

    for (let i = 0; i < requiredHcpFields.length; i++) {
      if (!user?.hcp[requiredHcpFields[i]]) return false;
    }
    return true;
  }, [hasRole, user, isAdmin, isSupport]);

  /**
   * Get user full name
   *
   * @param {boolean} withTitle If true, the hcp title is returned
   * @returns {string}
   */
  const getFullName = useCallback(
    (withTitle = false) => {
      let userDataKey;
      if (user.hcp) userDataKey = 'hcp';
      else if (user.lhcManager) userDataKey = 'lhcManager';

      if (userDataKey)
        return `${withTitle ? user[userDataKey]?.title : ''} ${user[userDataKey].name} ${
          user[userDataKey].surname
        }`.trim();

      return user.email;
    },
    [user]
  );

  const getIdentifier = useCallback(() => user.hcp?.identifier ?? '', [user]);

  return {
    ...user,
    ...(isAdmin && { hcp: {} }),
    isAuth,
    isAdmin,
    isLead: !!user.hcp?.lead ?? isAdmin,
    isValidator: !!user.hcp?.validator,
    hasRole,
    hasPermission,
    hasCompleteProfile,
    getRoleLabel: () => getRoleLabel(user.roles?.[0]),
    getFullName,
    getIdentifier,
    invalidateUserData: refresh,
    setActiveAccount,
    getActiveAccount,
  };
}
